import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) {
        // Parse Supabase errors properly
        if (error.message.includes('Invalid login credentials')) {
          return { 
            data: null, 
            error: { 
              message: 'Invalid email or password. Please check your credentials.',
              code: 'invalid_credentials'
            }
          };
        } else if (error.message.includes('Email not confirmed')) {
          return { 
            data: null, 
            error: { 
              message: 'Please verify your email address before signing in. Check your inbox for a confirmation link.',
              code: 'email_not_confirmed'
            }
          };
        }
        return { data: null, error: { message: error.message, code: error.code || 'unknown' } };
      }
      
      return { data, error: null };
    } catch (err) {
      return { 
        data: null, 
        error: { 
          message: 'An unexpected error occurred. Please try again.',
          code: 'unexpected_error'
        }
      };
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: { 
          data: metadata,
          // Disable email confirmation for development (change in production)
          emailRedirectTo: window.location.origin + '/auth/login'
        }
      });
      
      if (error) {
        // Parse Supabase errors properly
        if (error.message.includes('already registered')) {
          return { 
            data: null, 
            error: { 
              message: 'This email is already registered. Try logging in instead.',
              code: 'email_exists'
            }
          };
        } else if (error.message.includes('Password should be')) {
          return { 
            data: null, 
            error: { 
              message: 'Password must be at least 6 characters long.',
              code: 'weak_password'
            }
          };
        }
        return { data: null, error: { message: error.message, code: error.code || 'unknown' } };
      }

      // Check if email confirmation is required
      const needsEmailConfirmation = data.user && !data.session;
      
      return { 
        data, 
        error: null,
        needsEmailConfirmation 
      };
    } catch (err) {
      return { 
        data: null, 
        error: { 
          message: 'An unexpected error occurred. Please try again.',
          code: 'unexpected_error'
        }
      };
    }
  };

  const signInWithOAuth = async (provider) => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: window.location.origin + '/portal'
        }
      });
      
      if (error) {
        return { 
          data: null, 
          error: { 
            message: `Failed to sign in with ${provider}. Please try again.`,
            code: 'oauth_error'
          }
        };
      }
      
      return { data, error: null };
    } catch (err) {
      return { 
        data: null, 
        error: { 
          message: 'An unexpected error occurred. Please try again.',
          code: 'unexpected_error'
        }
      };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signInWithOAuth,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
